import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { get } from "../api/client";
import { useAuth } from "../state";
import { Navbar } from "../components/navbar";
import type { LeaderboardResponse, LeaderboardEntry, LeaderboardPeriod } from "../types";

const PAGE_SIZE = 10;

const PERIODS: { value: LeaderboardPeriod; labelKey: string }[] = [
  { value: "weekly", labelKey: "leaderboard.weekly" },
  { value: "monthly", labelKey: "leaderboard.monthly" },
  { value: "allTime", labelKey: "leaderboard.allTime" },
];

type LoadState = "loading" | "error" | "ready";

function LeaderboardList({
  entries,
  type,
  state,
}: {
  entries: LeaderboardEntry[];
  type: "earned" | "spent";
  state: LoadState;
}) {
  const { t } = useTranslation();
  const { studentId } = useAuth();
  const [page, setPage] = useState(1);

  // New data (period switch) restarts at page 1.
  useEffect(() => {
    setPage(1);
  }, [entries]);

  if (state === "loading") return <li className="loading">{t("common.loading")}</li>;
  if (state === "error") return <li className="error">{t("leaderboard.loadFailed")}</li>;
  if (entries.length === 0) return <li className="empty">{t("leaderboard.noData")}</li>;

  const start = (page - 1) * PAGE_SIZE;
  const pageEntries = entries.slice(start, start + PAGE_SIZE);
  const totalPages = Math.ceil(entries.length / PAGE_SIZE);

  return (
    <>
      {pageEntries.map((entry) => {
        const isCurrentUser = entry.studentId === studentId;
        const points = type === "earned" ? entry.pointsEarned : entry.pointsSpent;
        return (
          <li
            key={entry.studentId}
            className={`leaderboard-entry ${isCurrentUser ? "current-user" : ""}`}
          >
            <span className={`entry-rank ${entry.rank <= 3 ? `rank-${entry.rank}` : ""}`}>
              {entry.rank}
            </span>
            <div className="entry-info">
              <span className="entry-name">
                {entry.ninjaName ?? t("leaderboard.unknownNinja")}
              </span>
              {isCurrentUser && (
                <span className="you-badge">{t("leaderboard.you")}</span>
              )}
            </div>
            <span className="entry-points">
              {t("common.bux", { amount: Math.abs(points || 0) })}
            </span>
          </li>
        );
      })}
      {totalPages > 1 && (
        <li className="pagination">
          <button
            className="btn-page btn-prev"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {t("common.prev")}
          </button>
          <span className="page-info">
            {page} / {totalPages}
          </span>
          <button
            className="btn-page btn-next"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            {t("common.next")}
          </button>
        </li>
      )}
    </>
  );
}

export function LeaderboardPage() {
  const { t } = useTranslation();
  const { facilityId } = useAuth();
  const [period, setPeriod] = useState<LeaderboardPeriod>("weekly");
  const [earnedEntries, setEarnedEntries] = useState<LeaderboardEntry[]>([]);
  const [spentEntries, setSpentEntries] = useState<LeaderboardEntry[]>([]);
  const [earnedState, setEarnedState] = useState<LoadState>("loading");
  const [spentState, setSpentState] = useState<LoadState>("loading");

  useEffect(() => {
    let cancelled = false;
    setEarnedState("loading");
    setSpentState("loading");

    async function load() {
      const [earnedRes, spentRes] = await Promise.all([
        get<LeaderboardResponse>(
          `/facilities/${facilityId}/leaderboard/earned?period=${period}`,
        ),
        get<LeaderboardResponse>(
          `/facilities/${facilityId}/leaderboard/spent?period=${period}`,
        ),
      ]);
      if (cancelled) return;

      if (earnedRes.error || !earnedRes.data) {
        setEarnedEntries([]);
        setEarnedState("error");
      } else {
        setEarnedEntries(
          earnedRes.data.leaderboard.filter((entry) => (entry.pointsEarned ?? 0) > 0),
        );
        setEarnedState("ready");
      }

      if (spentRes.error || !spentRes.data) {
        setSpentEntries([]);
        setSpentState("error");
      } else {
        setSpentEntries(
          spentRes.data.leaderboard.filter(
            (entry) => Math.abs(entry.pointsSpent ?? 0) > 0,
          ),
        );
        setSpentState("ready");
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [facilityId, period]);

  return (
    <>
      <Navbar />
      <main className="leaderboard-page">
        <div className="dashboard-container">
          <div className="leaderboard">
            <header className="leaderboard-header">
              <h1>{t("leaderboard.title")}</h1>
              <div className="period-tabs">
                {PERIODS.map((p) => (
                  <button
                    key={p.value}
                    className={`period-tab ${period === p.value ? "active" : ""}`}
                    onClick={() => setPeriod(p.value)}
                  >
                    {t(p.labelKey)}
                  </button>
                ))}
              </div>
            </header>

            <div className="leaderboard-columns">
              <section className="leaderboard-column">
                <h2>{t("leaderboard.topEarners")}</h2>
                <ul className="leaderboard-list">
                  <LeaderboardList
                    entries={earnedEntries}
                    type="earned"
                    state={earnedState}
                  />
                </ul>
              </section>

              <section className="leaderboard-column">
                <h2>{t("leaderboard.topSpenders")}</h2>
                <ul className="leaderboard-list">
                  <LeaderboardList
                    entries={spentEntries}
                    type="spent"
                    state={spentState}
                  />
                </ul>
              </section>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
